import { dateStringSchema, financialHealthSummarySchema, financialQuestionAnswerSchema, financialReportResponseSchema, financialReportUnavailableSchema, fipThresholdOverridesSchema, fipThresholdsSchema, invoiceStatusSchema, llmChatMessageSchema, reportPeriodSchema as periodSchema, } from '@teo/types';
import { z } from 'zod';
import { displayId } from './common.js';
import { hasOwnershipIdSchema } from './ownership.js';
export const getBalanceSheetSchema = hasOwnershipIdSchema.extend({
    asOfDate: dateStringSchema,
});
export const getProfitAndLossSchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
});
export const getBudgetVsActualSchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
});
export const getArAgingSchema = hasOwnershipIdSchema.extend({
    reportDate: dateStringSchema.optional(),
});
export const getApAgingSchema = hasOwnershipIdSchema.extend({
    reportDate: dateStringSchema.optional(),
});
export const getBankReconciliationSchema = hasOwnershipIdSchema;
export const getGeneralLedgerSchema = hasOwnershipIdSchema.extend({
    startDate: dateStringSchema,
    endDate: dateStringSchema,
});
export { financialReportResponseSchema };
export const budgetNotConfiguredSchema = z.object({
    type: z.literal('BudgetNotConfigured'),
});
export const budgetVsActualOutputSchema = z.union([
    financialReportResponseSchema,
    budgetNotConfiguredSchema,
    financialReportUnavailableSchema,
]);
export const agingAmountsSchema = z.object({
    current: z.number(),
    days1to30: z.number(),
    days31to60: z.number(),
    days61to90: z.number(),
    days91AndOver: z.number(),
    total: z.number(),
});
const arAgingRowSchema = agingAmountsSchema.extend({
    externalCustomerId: z.string(),
    customerName: z.string(),
});
export const arAgingOutputSchema = z.object({
    reportDate: z.string(),
    fetchedAt: z.date(),
    rows: z.array(arAgingRowSchema),
    grandTotal: agingAmountsSchema,
});
const apAgingRowSchema = agingAmountsSchema.extend({
    externalVendorId: z.string(),
    vendorName: z.string(),
});
export const apAgingOutputSchema = z.object({
    reportDate: z.string(),
    fetchedAt: z.date(),
    rows: z.array(apAgingRowSchema),
    grandTotal: agingAmountsSchema,
});
export const financialReportReadOutputSchema = z.union([
    financialReportResponseSchema,
    financialReportUnavailableSchema,
]);
const arAgingPropertyGroupSchema = z.object({
    scope: z.discriminatedUnion('kind', [
        z.object({
            kind: z.literal('property'),
            propertyDisplayId: z.string(),
            propertyName: z.string(),
        }),
        z.object({ kind: z.literal('unassigned') }),
    ]),
    rows: z.array(arAgingRowSchema),
    subtotal: agingAmountsSchema,
});
export const arAgingReadOutputSchema = z.union([
    arAgingOutputSchema.extend({ propertyGroups: z.array(arAgingPropertyGroupSchema) }),
    financialReportUnavailableSchema,
]);
export const apAgingReadOutputSchema = z.union([
    apAgingOutputSchema,
    financialReportUnavailableSchema,
]);
export const refreshArAgingOutputSchema = arAgingOutputSchema.extend({
    persisted: z.boolean(),
});
export const refreshApAgingOutputSchema = apAgingOutputSchema.extend({
    persisted: z.boolean(),
});
export const apPendingLocalBucketSchema = z.enum([
    'current',
    'days1to30',
    'days31to60',
    'days61to90',
    'days91AndOver',
]);
const apPendingLocalRowSchema = z.object({
    invoiceDisplayId: z.string(),
    contractorName: z.string(),
    status: invoiceStatusSchema,
    dueDate: z.string().nullable(),
    invoiceAmount: z.number(),
    bucket: apPendingLocalBucketSchema,
});
export const apPendingLocalOutputSchema = z.object({
    reportDate: z.string(),
    rows: z.array(apPendingLocalRowSchema),
    grandTotal: agingAmountsSchema,
});
const apTeoAgingRowSchema = agingAmountsSchema.extend({
    vendorId: z.number().nullable(),
    vendorName: z.string(),
});
export const apTeoAgingOutputSchema = z.object({
    reportDate: z.string(),
    rows: z.array(apTeoAgingRowSchema),
    grandTotal: agingAmountsSchema,
});
export const apDefaultSourceSchema = z.enum(['teo', 'qbo', 'none']);
export const generateFIPSchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
    asOfDate: dateStringSchema,
    thresholds: fipThresholdOverridesSchema.optional(),
});
export const getLatestFIPSchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
});
const balanceSheetMetricsSchema = z.object({
    totalCurrentAssets: z.number().optional(),
    operatingCash: z.number(),
    reserveCash: z.number(),
    totalCash: z.number(),
    accountsReceivableBalance: z.number(),
    accountsPayableBalance: z.number(),
});
const arAgingMetricsSchema = z.object({
    current: z.number(),
    days31to60: z.number(),
    days61to90: z.number(),
    days90Plus: z.number(),
    totalAR: z.number(),
    delinquencyAmount: z.number(),
    delinquencyRate: z.number(),
});
const apAgingMetricsSchema = z.object({
    current: z.number(),
    days31to60: z.number(),
    days61to90: z.number(),
    days90Plus: z.number(),
    totalAP: z.number(),
    overdueAP: z.number(),
});
const budgetCategoryVarianceSchema = z.object({
    label: z.string(),
    section: z.string(),
    actual: z.number(),
    budget: z.number(),
    variance: z.number(),
    variancePct: z.number(),
});
const budgetVsActualMetricsSchema = z.object({
    totalRevenueActual: z.number(),
    totalRevenueBudget: z.number(),
    revenueVariance: z.number(),
    totalOpExActual: z.number(),
    totalOpExBudget: z.number(),
    opExVariance: z.number(),
    netOperatingIncomeActual: z.number(),
    netOperatingIncomeBudget: z.number(),
    monthlyOpExAvgActual: z.number(),
    monthlyOpExAvgBudget: z.number(),
    categories: z.array(budgetCategoryVarianceSchema),
    reserveContribution: z.number(),
});
const pnlLineSchema = z.object({
    label: z.string(),
    amount: z.number(),
});
const pnlMetricsSchema = z.object({
    totalRevenue: z.number(),
    totalExpenses: z.number(),
    netIncome: z.number(),
    incomeLines: z.array(pnlLineSchema),
    expenseLines: z.array(pnlLineSchema),
});
const computedMetricsSchema = z.object({
    operatingRunway: z.number().optional(),
    monthlyExpenses: z.number().optional(),
    delinquencyRate: z.number(),
    reserveCoverageRatio: z.number(),
    reserveCoveragePct: z.number().nullable(),
    arRiskFlag: z.boolean(),
    apPressureFlag: z.boolean(),
    budgetVarianceFlag: z.boolean(),
    liquidityFlag: z.boolean(),
});
export const financialIntelligencePayloadSchema = z.object({
    ownershipId: z.number(),
    generatedAt: z.date(),
    period: periodSchema,
    asOfDate: dateStringSchema,
    balanceSheet: balanceSheetMetricsSchema,
    arAging: arAgingMetricsSchema,
    apAging: apAgingMetricsSchema,
    budgetVsActual: budgetVsActualMetricsSchema.nullable(),
    profitAndLoss: pnlMetricsSchema.optional(),
    computed: computedMetricsSchema,
    thresholds: fipThresholdsSchema,
});
export const generateFinancialSummarySchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
    forceRefresh: z.boolean().optional().default(false),
    budgetDisplayId: displayId
        .optional()
        .describe('Measure against this budget instead of the active one.'),
});
export const getFinancialSummarySchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
});
export const askFinancialQuestionSchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
    question: z.string().min(1).max(500),
    history: z.array(llmChatMessageSchema).max(10),
    budgetDisplayId: displayId
        .optional()
        .describe('Measure against this budget instead of the active one.'),
});
export { financialHealthSummarySchema, financialQuestionAnswerSchema };
const approvalBaseSchema = hasOwnershipIdSchema.extend({
    period: periodSchema,
    fiscalYear: z
        .number()
        .int()
        .min(2000)
        .max(2100)
        .describe('The year the budget covers, as four digits. TEOAI has no fiscal start month, so this is the calendar year.'),
});
export const requestReportApprovalSchema = approvalBaseSchema.extend({
    pdfDocumentDisplayId: z.string().min(1),
});
export const approveReportSchema = approvalBaseSchema;
export const rejectReportSchema = approvalBaseSchema.extend({
    notes: z.string().max(1000).optional(),
});
export const getReportApprovalStatusSchema = approvalBaseSchema;
export const reportApprovalStatusEnum = z.enum(['pending', 'approved', 'rejected']);
export const addCommentSchema = approvalBaseSchema.extend({
    comment: z.string().max(2000),
});
export const getApprovalHistorySchema = approvalBaseSchema;
export const approvalHistoryItemSchema = z.object({
    id: z.number(),
    ownershipId: z.number(),
    period: periodSchema,
    fiscalYear: z.number(),
    pdfDocumentId: z.number().nullable(),
    pdfDocumentDisplayId: z.string().nullable(),
    approvedAt: z.date(),
    approvedByUserId: z.number().nullable(),
    requestedAt: z.date(),
});
const approvalSharedFields = {
    id: z.number(),
    ownershipId: z.number(),
    period: z.string(),
    fiscalYear: z.number(),
    pdfDocumentId: z.number().nullable(),
    pdfDocumentDisplayId: z.string().nullable(),
    requestedByUserId: z.number(),
    requestedAt: z.date(),
};
export const reportApprovalOutputSchema = z.discriminatedUnion('status', [
    z.object({
        ...approvalSharedFields,
        status: z.literal('pending'),
        resolvedByUserId: z.null(),
        resolvedAt: z.null(),
        rejectionNotes: z.null(),
    }),
    z.object({
        ...approvalSharedFields,
        status: z.literal('approved'),
        resolvedByUserId: z.number(),
        resolvedAt: z.date(),
        rejectionNotes: z.null(),
        comment: z.string().nullable(),
    }),
    z.object({
        ...approvalSharedFields,
        status: z.literal('rejected'),
        resolvedByUserId: z.number(),
        resolvedAt: z.date(),
        rejectionNotes: z.string(),
    }),
]);
//# sourceMappingURL=accountingReports.js.map